/**
 * simUploadQueueStore
 *
 * A framework-agnostic singleton that tracks "Override Template Sim File"
 * uploads that are running in the background (i.e. the Create/Edit Run modal
 * was closed before the browser finished uploading to Azure Blob temp storage).
 *
 * Flow:
 *  1. User clicks "Save & Continue in Background" while sim file is uploading.
 *  2. The run metadata is saved on the server with skipSimFileCopy=true.
 *  3. Caller registers the pending upload here via register().
 *  4. This store chains .then() on the upload promise; when done it calls the
 *     /finalize-sim-upload endpoint so the server does the
 *     blob-to-blob copy (fast Azure-internal operation).
 *  5. React components subscribe() to receive live status updates for the
 *     banner and notification bell badge.
 */

import { UploadStatus } from '../utils/constants'
import { UploadResult } from './blobUploadService'
import { apiClient } from '../api/configs/axios-setup'

export type SimUploadStatus =
  | 'uploading'
  | 'finalizing'
  | 'decompressing'
  | 'done'
  | 'failed'
  | 'cancelled'

export type PendingSimUpload = {
  runId: string
  runName: string
  fileName: string
  status: SimUploadStatus
  /** Upload percentage 0-100, only meaningful while status === 'uploading' */
  progress?: number
  error?: string
}

type Listener = (uploads: PendingSimUpload[]) => void

const STORAGE_KEY = 'sim-upload-notifications'
const MAX_SAVED_NOTIFICATIONS = 5

let pendingUploads: PendingSimUpload[] = []
const listeners: Set<Listener> = new Set()

// Large uploads can outlive the access token, so finalize needs a fresh one (set from index.tsx).
let tokenRefresher: (() => Promise<void>) | null = null

/**
 * Load saved notifications from localStorage
 */
const loadSavedNotifications = (): PendingSimUpload[] => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      const parsed = JSON.parse(saved) as PendingSimUpload[]
      // Only load terminal states (done, failed, cancelled)
      return parsed.filter(
        u => u.status === 'done' || u.status === 'failed' || u.status === 'cancelled'
      )
    }
  } catch (err) {
    console.error('Failed to load saved notifications:', err)
  }
  return []
}

/**
 * Save completed notifications to localStorage (last 5 only)
 */
const saveNotifications = (uploads: PendingSimUpload[]): void => {
  try {
    // Only save terminal states
    const completed = uploads.filter(
      u => u.status === 'done' || u.status === 'failed' || u.status === 'cancelled'
    )
    // Keep only the last 5
    const toSave = completed.slice(-MAX_SAVED_NOTIFICATIONS)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave))
  } catch (err) {
    console.error('Failed to save notifications:', err)
  }
}

// Initialize with saved notifications
pendingUploads = loadSavedNotifications()

const notify = (): void => {
  const snapshot = [...pendingUploads]
  listeners.forEach(l => {
    try {
      l(snapshot)
    } catch (_) {
      // swallow listener errors so one bad subscriber can't break others
    }
  })
}

/**
 * Simple throttle helper to limit how often notify() is called for progress updates.
 * Ensures the last value is always emitted after the throttle period.
 */
const createThrottle = (fn: () => void, wait: number) => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null
  let lastCallTime = 0
  let pendingCall = false

  return () => {
    const now = Date.now()
    const timeSinceLastCall = now - lastCallTime

    if (timeSinceLastCall >= wait) {
      // Enough time has passed, call immediately
      lastCallTime = now
      fn()
      pendingCall = false
    } else {
      // Too soon, schedule for later (ensures we don't lose the final update)
      pendingCall = true
      if (!timeoutId) {
        timeoutId = setTimeout(() => {
          timeoutId = null
          if (pendingCall) {
            lastCallTime = Date.now()
            fn()
            pendingCall = false
          }
        }, wait - timeSinceLastCall)
      }
    }
  }
}

// Throttled notify for progress updates only (max 10 updates/sec)
const throttledNotify = createThrottle(notify, 100)

const simUploadQueueStore = {
  setTokenRefresher(fn: (() => Promise<void>) | null): void {
    tokenRefresher = fn
  },

  /**
   * Register a file upload that should complete in the background.
   *
   * @param params        Metadata about the run and the file being uploaded.
   * @param uploadPromise The Promise returned by uploadMultipleFilesWithConcurrency.
   *                      This store will chain .then() on it; the caller may
   *                      also chain independently – that's safe.
   */
  register(
    params: {
      /** Unique tracking key for this entry in the store (e.g. `${runId}` or `${runId}-geo`). */
      runId: string
      /** Actual MongoDB run _id used in the finalize API call. Defaults to runId. */
      apiRunId?: string
      runName: string
      fileName: string
      aeroTempRunId: string
      gmid: string
      /** Server endpoint path segment for finalization. Defaults to 'finalize-sim-upload'. */
      finalizeEndpoint?: string
    },
    uploadPromise: Promise<UploadResult[]>
  ): void {
    // Avoid duplicate registrations for the same tracking key
    if (pendingUploads.find(u => u.runId === params.runId)) {
      return
    }

    const entry: PendingSimUpload = {
      runId: params.runId,
      runName: params.runName,
      fileName: params.fileName,
      status: 'uploading'
    }
    pendingUploads = [...pendingUploads, entry]
    notify()

    uploadPromise
      .then(async (results: UploadResult[]) => {
        // uploadMultipleFilesWithConcurrency always resolves (never rejects).
        // Check if the single sim file actually succeeded.
        const success = results.some(r => r.success)
        const cancelled = results.every(r => !r.success && r.error === UploadStatus.CANCELLED)

        if (cancelled) {
          // Save cancelled state before removing
          const cancelledEntry = pendingUploads.find(u => u.runId === params.runId)
          if (cancelledEntry) {
            pendingUploads = pendingUploads.map(u =>
              u.runId === params.runId ? { ...u, status: 'cancelled' } : u
            )
            saveNotifications(pendingUploads)
          }
          pendingUploads = pendingUploads.filter(u => u.runId !== params.runId)
          notify()
          return
        }

        if (!success) {
          const errorMsg = results.find(r => !r.success && r.error !== UploadStatus.CANCELLED)
            ?.error
          pendingUploads = pendingUploads.map(u =>
            u.runId === params.runId
              ? { ...u, status: 'failed', error: errorMsg || 'Upload failed' }
              : u
          )
          saveNotifications(pendingUploads)
          notify()
          return
        }

        // File is in temp storage – trigger server-side blob-to-blob copy
        pendingUploads = pendingUploads.map(u =>
          u.runId === params.runId ? { ...u, status: 'finalizing' } : u
        )
        notify()

        const apiRunId = params.apiRunId ?? params.runId
        const endpoint = params.finalizeEndpoint ?? 'finalize-sim-upload'
        console.log('Finalizing SIM upload for runId:', params.runId)
        try {
          await tokenRefresher?.()
        } catch (refreshErr) {
          console.warn('Token refresh before finalize failed', refreshErr)
        }
        const finalizeRes: any = await apiClient.post(
          `/WorkflowSubmissions/${apiRunId}/${endpoint}`,
          {
            aeroTempRunId: params.aeroTempRunId,
            gmid: params.gmid
          }
        )
        // The axios 401 interceptor resolves with the error instead of rejecting.
        const finalizeStatus = finalizeRes?.status ?? finalizeRes?.response?.status
        if (finalizeRes?.isAxiosError || !finalizeStatus || finalizeStatus >= 400) {
          throw new Error(
            finalizeStatus === 401
              ? 'Session expired before the upload could be finalized. Sign in and re-upload the sim file.'
              : `Finalize failed${finalizeStatus ? ` (HTTP ${finalizeStatus})` : ''}`
          )
        }

        pendingUploads = pendingUploads.map(u =>
          u.runId === params.runId ? { ...u, status: 'done' } : u
        )
        saveNotifications(pendingUploads)
        notify()

        // Keep done notifications visible - user must manually dismiss them
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : 'Unexpected error'
        pendingUploads = pendingUploads.map(u =>
          u.runId === params.runId ? { ...u, status: 'failed', error: message } : u
        )
        saveNotifications(pendingUploads)
        notify()
      })
  },

  /**
   * Register a simple upload (no finalize step) that should complete in the background.
   * Suitable for direct-to-blob uploads like Ride Height repositories.
   *
   * @param params        Metadata about the upload.
   * @param uploadPromise A Promise that resolves when the upload is done, or rejects on failure.
   */
  registerSimple(
    params: {
      runId: string
      runName: string
      fileName: string
    },
    uploadPromise: Promise<void>
  ): void {
    if (pendingUploads.find(u => u.runId === params.runId)) {
      return
    }

    const entry: PendingSimUpload = {
      runId: params.runId,
      runName: params.runName,
      fileName: params.fileName,
      status: 'uploading'
    }
    pendingUploads = [...pendingUploads, entry]
    notify()

    uploadPromise
      .then(() => {
        pendingUploads = pendingUploads.map(u =>
          u.runId === params.runId ? { ...u, status: 'done', progress: 100 } : u
        )
        saveNotifications(pendingUploads)
        notify()
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : 'Unexpected error'
        pendingUploads = pendingUploads.map(u =>
          u.runId === params.runId ? { ...u, status: 'failed', error: message } : u
        )
        saveNotifications(pendingUploads)
        notify()
      })
  },

  /**
   * Register a backend decompression step that should complete in the background.
   * Suitable for server-side work the client kicks off and waits on, such as the
   * Ride Height finalize endpoint that decompresses a staged archive and moves
   * the files to their final location.
   *
   * @param params           Metadata about the work being finalized.
   * @param decompressPromise A Promise that resolves when the backend finishes,
   *                          or rejects on failure.
   */
  registerDecompression(
    params: {
      runId: string
      runName: string
      fileName: string
    },
    decompressPromise: Promise<void>
  ): void {
    if (pendingUploads.find(u => u.runId === params.runId)) {
      return
    }

    const entry: PendingSimUpload = {
      runId: params.runId,
      runName: params.runName,
      fileName: params.fileName,
      status: 'decompressing',
      progress: 0
    }
    pendingUploads = [...pendingUploads, entry]
    notify()

    decompressPromise
      .then(() => {
        // For each pending upload, if the runId matches, mark it as done and set progress to 100
        pendingUploads = pendingUploads.map(u =>
          u.runId === params.runId ? { ...u, status: 'done', progress: 100 } : u
        )
        // Save the updated notifications to localStorage and notify listeners
        saveNotifications(pendingUploads)
        notify()
      })
      .catch((err: unknown) => {
        // For each pending upload, if there is an error, mark it as failed and store the error message
        const message = err instanceof Error ? err.message : 'Unexpected error'
        pendingUploads = pendingUploads.map(u =>
          u.runId === params.runId ? { ...u, status: 'failed', error: message } : u
        )
        saveNotifications(pendingUploads)
        notify()
      })
  },

  /** Get a point-in-time snapshot of all pending uploads. */
  getAll(): PendingSimUpload[] {
    return [...pendingUploads]
  },

  /** Manually dismiss a completed/failed entry from the list. */
  dismiss(runId: string): void {
    pendingUploads = pendingUploads.filter(u => u.runId !== runId)
    saveNotifications(pendingUploads)
    notify()
  },

  /**
   * Subscribe to changes.
   * @returns An unsubscribe function – call it in a useEffect cleanup.
   */
  subscribe(listener: Listener): () => void {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  },

  /**
   * Update the progress percentage for an upload that is actively in-flight.
   * Called by the parent page after register() wires up the onProgressUpdate hook.
   * Uses throttled notifications to avoid excessive re-renders (max 10 updates/sec).
   */
  updateProgress(runId: string, percent: number): void {
    pendingUploads = pendingUploads.map(u => (u.runId === runId ? { ...u, progress: percent } : u))
    throttledNotify() // Throttled to prevent 100+ updates/sec
  },

  /**
   * Update the display label (fileName) for an entry. Useful when the final
   * label (e.g. the compressed zip size) is only known after the entry has
   * already been registered.
   */
  updateFileName(runId: string, fileName: string): void {
    pendingUploads = pendingUploads.map(u => (u.runId === runId ? { ...u, fileName } : u))
    notify()
  },

  /** True if any uploads are actively in-progress (uploading, finalizing or decompressing). */
  hasActiveUploads(): boolean {
    return pendingUploads.some(
      u => u.status === 'uploading' || u.status === 'finalizing' || u.status === 'decompressing'
    )
  },

  /** True if a specific run has an active upload (uploading, finalizing or decompressing). */
  hasActiveUploadForRun(runId: string): boolean {
    return pendingUploads.some(
      u =>
        u.runId === runId &&
        (u.status === 'uploading' || u.status === 'finalizing' || u.status === 'decompressing')
    )
  },

  /** Clear all saved notifications from localStorage and memory. */
  clearAll(): void {
    pendingUploads = []
    saveNotifications([])
    notify()
  }
}

export default simUploadQueueStore
